import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceAssistant } from './WorkspaceAssistantContext';

const SEVERITY_COLOR = { critical: 'red', warning: 'orange', info: 'blue' };
const TABS = ['all', 'system', 'update'];

const formatTime = (value, zh) => {
  if (!value) return zh ? '实时' : 'Live';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(zh ? 'zh-CN' : 'en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Bell + unread badge + feed (Novu Inbox pattern): live alerts first, then
// stored platform updates and system notices. Hidden unless the workspace
// assistant is turned on for this merchant.
const NotificationFeed = ({ ctx, zh, placement, showLabel }) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tab, setTab] = useState(0);
  const { feed, markRead, markAllRead } = ctx;
  const filter = TABS[tab];
  const items = feed.items.filter((item) => filter === 'all' || item.category === filter);
  const label = zh ? '通知' : 'Notifications';
  const badge = feed.unread_count > 0 && (
    <Badge colorScheme="red" borderRadius="full" fontSize="0.65rem" px={1.5} data-testid="notification-unread">
      {feed.unread_count > 99 ? '99+' : feed.unread_count}
    </Badge>
  );

  const openItem = (item) => {
    markRead(item);
    if (!item.link_path) return;
    onClose(); // the panel would otherwise stay open over the page it just opened
    navigate(ctx.mapLink ? ctx.mapLink(item.link_path) : item.link_path);
  };

  return (
    <Popover placement={placement} isLazy isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        {showLabel ? (
          <Button variant="ghost" size="sm" w="full" justifyContent="flex-start" color="gray.600"
            leftIcon={<FiBell size={16} />} iconSpacing={3} aria-label={label}>
            <HStack spacing={2} flex="1" justify="space-between"><Text>{label}</Text>{badge}</HStack>
          </Button>
        ) : (
          <Box position="relative" display="inline-flex">
            <IconButton variant="ghost" size="sm" icon={<FiBell size={18} />} aria-label={label} />
            {badge && <Box position="absolute" top="-2px" right="-4px" pointerEvents="none">{badge}</Box>}
          </Box>
        )}
      </PopoverTrigger>
      <Portal>
        <PopoverContent w={{ base: '92vw', sm: '380px' }} maxH="70vh" overflow="hidden" display="flex" flexDirection="column">
          <PopoverHeader>
            <HStack justify="space-between">
              <Text fontWeight="semibold">{label}</Text>
              <Button size="xs" variant="link" colorScheme="blue" onClick={markAllRead}
                isDisabled={!feed.items.some((item) => item.kind === 'stored' && !item.read)}>
                {zh ? '全部已读' : 'Mark all read'}
              </Button>
            </HStack>
            <Tabs size="sm" variant="soft-rounded" colorScheme="blue" mt={2} index={tab} onChange={setTab}>
              <TabList>
                <Tab>{zh ? '全部' : 'All'}</Tab>
                <Tab>{zh ? '系统' : 'System'}</Tab>
                <Tab>{zh ? '平台更新' : 'Updates'}</Tab>
              </TabList>
            </Tabs>
          </PopoverHeader>
          <PopoverBody overflowY="auto" p={0}>
            {items.length === 0 ? (
              <Text p={6} textAlign="center" color="gray.500" fontSize="sm">{zh ? '暂无通知' : 'Nothing here'}</Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {items.map((item) => (
                  <Box key={item.id} as="button" type="button" textAlign="left" px={4} py={3}
                    borderBottom="1px" borderColor="gray.100" bg={item.read ? 'white' : 'blue.50'}
                    _hover={{ bg: 'gray.50' }} onClick={() => openItem(item)}>
                    <HStack spacing={2} mb={1}>
                      <Box w="8px" h="8px" borderRadius="full" bg={`${SEVERITY_COLOR[item.severity] || 'gray'}.400`} flexShrink={0} />
                      <Text fontSize="sm" fontWeight={item.read ? 'normal' : 'semibold'} noOfLines={2} flex="1">
                        {zh ? item.title_zh : item.title_en}
                      </Text>
                    </HStack>
                    {(zh ? item.body_zh : item.body_en) && (
                      <Text fontSize="xs" color="gray.600" noOfLines={3} pl={4}>{zh ? item.body_zh : item.body_en}</Text>
                    )}
                    <Text fontSize="xs" color="gray.400" pl={4} mt={1}>{formatTime(item.created_at, zh)}</Text>
                  </Box>
                ))}
              </VStack>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// floating: pinned above the assistant button, for shells without a sidebar slot (e-commerce).
const NotificationBell = ({ zh = true, placement = 'right-end', showLabel = false, floating = false }) => {
  const ctx = useWorkspaceAssistant();
  if (!ctx?.enabled) return null;
  const feed = <NotificationFeed ctx={ctx} zh={zh} placement={placement} showLabel={showLabel} />;
  return floating ? (
    <Box position="fixed" right={{ base: 5, md: 7 }} bottom={{ base: 16, md: 20 }} zIndex="docked"
      bg="white" borderRadius="full" shadow="md" data-testid="notification-bell-floating">
      {feed}
    </Box>
  ) : feed;
};

export default NotificationBell;
